<template>
  <form
      ref="formRef"
      :action="action"
      :method="method"
      class="form"
      @submit="onSubmit"
  >

    <FormErrors v-if="!disableFormErrors" />

    <input v-if="csrfToken" type="hidden" name="_token" :value="csrfToken" />

    <div v-if="warnings.length"  class="p-2">
      <div v-for="warning in warnings" class="alert alert-warning mb-0">
        {{ warning }}
      </div>
    </div>


    <div v-if="progress" class="content-loader">
      <Spinner />
    </div>

    <slot v-bind="{ data, errors, initiated: config.initiated, submit: onSubmit }" />
  </form>
</template>
<script setup>
import {inject, nextTick, provide, reactive, ref, watch, computed} from "vue";
import Spinner from "@/js/Elements/Spinner.vue";
import axios from "axios";
import FormErrors from "@/js/Elements/Form/FormErrors.vue";
import {useNotify} from "@/js/Plugins/Notify.js";
import {useI18n} from "vue-i18n";
import ConfirmModal from "@/js/Components/Modal/ConfirmModal.vue";
import {useModal} from "@/js/Plugins/Modal.js";

const emitter = inject('emitter');
const modal = useModal();
const emit = defineEmits(['success', 'submit', 'error', 'dataUpdate']);
const { t } = useI18n();
const notify = useNotify();
const props = defineProps({
  action: String,
  method: {
    type: String,
    default: 'post'
  },
  data: Object,
  appendData: Boolean,
  loading: Boolean,
  errors: Array,
  disableFormErrors: Boolean,
  successNotification: Boolean,
  confirmable: Boolean,
});
const errors = reactive({});
const warnings = ref([]);
const fields = reactive([]);
const events = ref([]);
const files = reactive({});
const data = reactive(props.data || {});
const config = reactive({
  url: props.action,
  method: props.method,
  initiated: true
});
const progress = ref(false);
const payload = ref();
const csrfToken = computed(() => data?._token || window.setup.csrf_token);

provide('form:data', data);
provide('form:errors', errors);
provide('form:warnings', warnings);
provide('form:fields', fields);
provide('form:add-field', addField);
provide('form:config', config);
provide('form:events', events);
provide('form:files', files);
provide('form:getFormData', getFormData);
provide('form:progress', progress);


watch(() => props.errors, (value) => {
  if (!value) return clearErrors();
  parseErrors(value);
}, { deep: true });

watch(() => props.data,(value) => {
  if (!value) return;
  Object.assign(data, value);
}, { deep: true });

watch(() => data, (value) => {
  emit('dataUpdate', value);
}, { deep: true});

watch(() => props.loading, (value) => progress.value = value);
watch(() => props.action, (value) => config.url = value);
watch(() => props.method, (value) => config.method = value);

const formRef = ref();
defineExpose({
  submit: onSubmit,
  singleSubmit,
  getData: () => data,
  getFiles: () => files,
  getFormData,
  getConfig: () => config,
  parseErrors,
  clearErrors,
  setErrors(err) {
    Object.keys(err).forEach(key => {
      errors[key] = err[key];
    })
  },
  fill(value) {
    Object.assign(data, value);
  }
});

function onSubmit(e) {
  if (e) {
    e.preventDefault();
  }
  return new Promise((resolve, reject) => {
    submit({...config}).then(resolve).catch(reject);
  });
}

function submit(configs, data = {}, confirmed = false) {
  return new Promise((resolve, reject) => {
    if (props.confirmable && !confirmed) {
      modal.open(ConfirmModal,{
        onConfirm({close}) {
          submit(configs, data, true);
          close();
        }
      });
      return;
    }

    emit('submit')

    if (!configs.url) return resolve();
    clearErrors();

    const requestConfig = configs;
    const formData = getFormData(requestConfig.method);
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });

    progress.value = true;

    requestConfig.method = requestConfig.method.toString().toLowerCase() === 'put' ? 'post' : requestConfig.method;
    requestConfig.data = formData;
    requestConfig.headers = { form: true };
    axios(requestConfig).then((response) => {
      events.value.forEach((event, index) => {
        try {
          emitter.emit(event, response);
        } catch (error) {
          console.error(error);
        }

      });

      if (props.successNotification) {
        notify.success(t('front.successfully_saved'));
      }
      emit('success', response.data);
      resolve(response);
    }).catch((error) => {
      const responseData = error?.response?.data;

      if (responseData?.warnings) {
        warnings.value = Array.isArray(responseData.warnings) ? responseData.warnings : [responseData.warnings];
        emit('warning', warnings.value);
        return;
      }

      if (!responseData?.errors && !responseData?.error) {
        return emit('error', error);
      }

      emit('error', responseData);
      parseErrors(responseData?.errors || responseData?.error);
      nextTick(scrollToInvalidField);
      reject(error);
    }).finally(() => {
      progress.value = false;
    });
  });
}

function singleSubmit(options, data = {}) {
  return submit(Object.assign({...config}, options), data);
}

function getFormData(method) {
  if (!formRef.value) return new FormData();

  const formData = new FormData(formRef.value);
  appendDataToFormData(data, formData);
  formData.append('_method', method || 'post');

  if (Object.keys(files).length) {
    Object.keys(files).forEach((field) => {
      const data = files[field];
      (Array.isArray(data) ? data : [data]).forEach((file) => {
        formData.append(field, file);
      })
    })
  }
  return formData;
}

function appendDataToFormData(data, formData) {
  if (!props.appendData) return;

  Object.keys(data).forEach((key) => {
    let field = key;
    if (formData.has(field)) return;

    if (Array.isArray(data[key])) {
      field = `${field}[]`;
      if (formData.has(field)) return;
      data[key].forEach(value => {
        if (!value) return;
        formData.append(field, value);
      });
      return;
    }

    if (data[key] && typeof data[key] === 'object') {
      Object.keys(data[key]).forEach(k => {
        const subField = `${field}[${k}]`;
        if (formData.has(subField)) return;
        const value = data[key][k];
        if (value === null || value === undefined || value === '') return;
        formData.append(subField, value);
      });
      return;
    }

    if (!data[key]) return;
    formData.append(field, data[key]);
  })
}

function scrollToInvalidField() {
  const field = formRef.value.querySelector('.input-group.is-invalid');
  if (!field) return;
  field.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function parseErrors(messages) {
  if (!messages) return;
  Object.keys(messages).forEach((key) => {
    errors[key] = messages[key][0];
  });
}

function addField(name) {
  const index = fields.indexOf(name);
  if (index !== -1) return;
  fields.push(name);
}

function clearErrors() {
  Object.keys(errors).forEach(key => delete errors[key]);
  warnings.value = [];
}
</script>

<style lang="scss">
@import "@/css/functions";
@import "@/css/variables";

.form {
  position: relative;

  .container {
    padding-left: 0;
    padding-right: 0;
  }

  .container > .row + .row {
    margin-top: toRem(24);
  }

  &-label {
    font-weight: $input-label-font-weight;
    font-size: $input-label-font-size;
  }

  &-text {
    color: themeColor('neutral-700') !important;
  }
}
</style>